import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import {
  useOrganizationsControllerGetAll,
  useWorkspacesControllerGetAll,
} from "@/api/__generated__/orval/accounts";
import { getWorkspacesToDisplay } from "@/helpers/rolePermissions/getWorkspacesToDisplay";
import {
  currentAccountIdAtom,
  currentWorkspaceIdAtom,
  organizationsAtom,
  userAtom,
  workspacesAtom,
} from "@/store";
import { useAuth } from "./authentification";
import { useRolePermission } from "./rolePermission/useRolePermission";

export const useAccountData = () => {
  const { isAuthenticated } = useAuth();

  const currentAccountId = useAtomValue(currentAccountIdAtom);
  const currentUser = useAtomValue(userAtom);

  const [currentWorkspaceId, setCurrentWorkspaceId] = useAtom(
    currentWorkspaceIdAtom
  );
  const setOrganizations = useSetAtom(organizationsAtom);
  const setWorkspaces = useSetAtom(workspacesAtom);

  const { isBrokerAccount, isAdvertiserAccount, isKazaarAccount } =
    useRolePermission();

  const { data: orgsData, isLoading: isLoadingOrgs } =
    useOrganizationsControllerGetAll(
      currentAccountId,
      { size: 100 },
      { query: { enabled: isAuthenticated && !!currentAccountId } }
    );

  const { data: workspacesData, isLoading: isLoadingWorkspaces } =
    useWorkspacesControllerGetAll(
      currentAccountId,
      { size: 200 },
      { query: { enabled: isAuthenticated && !!currentAccountId } }
    );

  useEffect(() => {
    const orgs = orgsData?.data?.data;
    if (orgs) {
      setOrganizations(orgs);
    }
  }, [orgsData, setOrganizations]);

  useEffect(() => {
    const wks = workspacesData?.data?.data;
    const orgs = orgsData?.data?.data;

    if (!wks) {
      return;
    }

    setWorkspaces(wks);

    if (isBrokerAccount || isKazaarAccount) {
      // Broker: default to first partnership workspace, not first own workspace
      const userWorkspaces = currentUser
        ? getWorkspacesToDisplay(currentUser)
        : [];
      const partnershipWorkspaceIds = [
        ...new Set((orgs ?? []).flatMap((org) => org.workspacesPartnership)),
      ];
      const firstPartnershipId = partnershipWorkspaceIds
        .map((id) => userWorkspaces.find((w) => w.workspaceId === id))
        .find(
          (w): w is { workspaceId: string; accountId: string } => !!w
        )?.workspaceId;
      const isCurrentValid =
        partnershipWorkspaceIds.includes(currentWorkspaceId);
      if (!isCurrentValid) {
        setCurrentWorkspaceId(firstPartnershipId ?? "");
      }
    } else {
      const isCurrentWorkspaceValid = wks.some(
        (w) => w.id === currentWorkspaceId
      );
      if (!isCurrentWorkspaceValid) {
        setCurrentWorkspaceId(wks[0]?.id ?? "");
      }
    }
  }, [
    workspacesData,
    orgsData,
    setWorkspaces,
    currentWorkspaceId,
    setCurrentWorkspaceId,
    isBrokerAccount,
    currentUser,
  ]);

  const isLoading = isLoadingOrgs || isLoadingWorkspaces;
  const hasNoWorkspace =
    isAdvertiserAccount &&
    !isLoading &&
    !!currentAccountId &&
    !workspacesData?.data?.data?.length;

  return { isLoading, hasNoWorkspace };
};
